// Lógica pura da tela de Diferimentos (sem antd, para rodar no jest do projeto).
//
// A conta de verdade é do servidor (dre-engine/handlers/diferimento.js) — aqui
// fica só o que a tela precisa para mostrar a prévia antes de confirmar.

export interface LancamentoDaCompra {
  id: string;
  descricao: string;
  fornecedor: string | null;
  valor: number;
  vencimento: string | null;
  problema: string | null;
}

export interface PreviaDiferimento {
  quantidade: number;
  total: number;
  valor_mensal: number | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  meses: number | null;
  categoria_dre: string | null;
  grupo_dre: string | null;
  lancamentos: LancamentoDaCompra[];
  problemas: string[];
}

export interface DiferimentoAtivo {
  id: string;
  lancamento_id: string;
  quantidade: number;
  fornecedor: string | null;
  descricao: string;
  valor_total: number;
  valor_mensal: number;
  periodo_inicio: string;
  periodo_fim: string;
  categoria_dre: string;
  grupo_dre: string | null;
  meses: number;
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export function periodoValido(p: string | null | undefined): boolean {
  return /^\d{4}\/(0[1-9]|1[0-2])$/.test(String(p || ''));
}

const indice = (p: string) => { const [a, m] = p.split('/').map(Number); return a * 12 + (m - 1); };

// '2025/10' → 'out/2025'
export function rotuloPeriodo(p: string | null | undefined): string {
  if (!periodoValido(p)) return '';
  const [a, m] = String(p).split('/').map(Number);
  return `${MESES[m - 1]}/${a}`;
}

// Último mês da janela (inclusive): início 2025/10 + 36 meses → 2028/09.
export function periodoFim(inicio: string, meses: number): string {
  const i = indice(inicio) + meses - 1;
  return `${Math.floor(i / 12)}/${String((i % 12) + 1).padStart(2, '0')}`;
}

export function pedidoValido(inicio: string | null, meses: number | null): boolean {
  if (!periodoValido(inicio)) return false;
  const ano = Number(String(inicio).slice(0, 4));
  if (ano < 2015 || ano > 2100) return false;          // ano digitado errado (2205) some com a despesa
  return Number.isInteger(meses) && (meses as number) >= 2 && (meses as number) <= 120;
}

// O que a pessoa digitou no campo "meses" → número inteiro, ou null. O campo é
// texto de propósito: um campo numérico com mínimo/máximo "conserta" o valor
// sozinho ao perder o foco, e o diferimento sairia com um prazo que a pessoa
// nunca viu na prévia.
export function mesesDoTexto(txt: string | null | undefined): number | null {
  const t = String(txt ?? '').trim();
  return /^\d{1,3}$/.test(t) ? Number(t) : null;
}

// Parcela mensal para EXIBIR, arredondada como o servidor arredonda
// (Math.round(v * 100) / 100 em handlers/diferimento.js). A DRE usa o valor
// cheio; isto é só o número da prévia, que tem que ser o mesmo da lista.
export function valorMensal(total: number, meses: number): number {
  return Math.round((Math.abs(Number(total) || 0) / meses) * 100) / 100;
}

// Quantos meses da janela já passaram (inclusive o mês de hoje) — é o que já
// foi para a DRE. Antes do início: 0. Depois do fim: todos.
export function mesesReconhecidos(d: { periodo_inicio: string; periodo_fim: string }, hoje: string): number {
  if (!periodoValido(d.periodo_inicio) || !periodoValido(d.periodo_fim) || !periodoValido(hoje)) return 0;
  const total = indice(d.periodo_fim) - indice(d.periodo_inicio) + 1;
  const passados = indice(hoje) - indice(d.periodo_inicio) + 1;
  return Math.max(0, Math.min(total, passados));
}

// Primeiro mês da DRE que o diferimento altera: o mais antigo entre o mês da
// própria despesa (que perde o valor inteiro) e o mês em que a diluição começa.
export function primeiroMesAfetado(inicio: string | null, original?: string | null): string | null {
  const validos = [inicio, original].filter(periodoValido) as string[];
  if (!validos.length) return null;
  return validos.reduce((a, b) => (indice(b) < indice(a) ? b : a));
}

// O diferimento muda algum mês que já passou? Então ele mexe numa DRE que
// alguém já olhou — a tela avisa antes de confirmar. Conta também o mês da
// PRÓPRIA despesa: seguro pago em agosto e diluído a partir de novembro tira a
// despesa de agosto.
export function mexeNoPassado(inicio: string | null, hoje: string, original?: string | null): boolean {
  const primeiro = primeiroMesAfetado(inicio, original);
  return !!primeiro && periodoValido(inicio) && periodoValido(hoje) && indice(primeiro) < indice(hoje);
}

// Nome da compra na lista: fornecedor quando são várias parcelas (a descrição
// de cada uma é "7/24 - …"), a própria descrição quando é uma só.
export function nomeDaCompra(d: { quantidade: number; fornecedor: string | null; descricao: string }): string {
  if (d.quantidade > 1 && d.fornecedor) return `${d.fornecedor} (${d.quantidade} parcelas)`;
  return d.descricao || d.fornecedor || 'Despesa diferida';
}

// A busca devolve no máximo isto (o servidor corta, da mais nova para a mais
// antiga). Chegou no teto → a tela avisa que pode haver mais.
export const LIMITE_BUSCA = 100;

export function brl(v: number | null | undefined): string {
  return 'R$ ' + Math.abs(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
